import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";

import { MyAccountData } from "../../../models/myaccount";
import { ProfileUpdateRequest } from "../../../models/user";

import { profileApi } from "../../../api/profile.api";
import { securityApi } from "../../../api/security.api";
import { useAuth } from "./AuthContext";
import { preferencesApi } from "../../../api/preferences.api"; // orthographe corrigée

interface MyAccountContextType {
  data: MyAccountData | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  refreshEmails: () => Promise<void>;
  refreshPhones: () => Promise<void>;
  refreshSessions: () => Promise<void>;
  refreshDevices: () => Promise<void>;
  refreshPreferences: () => Promise<void>;
  updateProfile: (updates: ProfileUpdateRequest) => Promise<void>;
}

const MyAccountContext = createContext<MyAccountContextType | undefined>(
  undefined,
);

export const MyAccountProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { status, isAuthenticated } = useAuth();

  const [data, setData] = useState<MyAccountData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const initialLoadRef = useRef(false);
  const isLoadingRef = useRef(false);

  // 🔒 Locks pour éviter les doubles appels simultanés
  const refreshLocks = useRef({
    profile: false,
    emails: false,
    phones: false,
    sessions: false,
    devices: false,
    preferences: false,
  });

  // Chargement complet
  const loadAllData = useCallback(async () => {
    if (!isAuthenticated) return;
    if (isLoadingRef.current) return;

    isLoadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const [
        profileRes,
        emailsRes,
        phonesRes,
        sessionsRes,
        devicesRes,
        preferencesRes,
      ] = await Promise.all([
        profileApi.getProfile(),
        profileApi.getEmails(),
        profileApi.getPhones(),
        securityApi.getSessions(),
        securityApi.getDevices(),
        preferencesApi.getPreferences(),
      ]);

      setData({
        profile: (profileRes.data as any) ?? null,
        emails: emailsRes.data ?? [],
        phones: phonesRes.data ?? [],
        sessions: (sessionsRes.data as any) ?? [],
        devices: (devicesRes.data as any) ?? [],
        preferences: preferencesRes.data ?? null,
      });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Erreur lors du chargement",
      );
    } finally {
      isLoadingRef.current = false;
      setLoading(false);
    }
  }, [isAuthenticated]);

  // ---------- Refresh individuels avec lock ----------

  const refreshProfile = useCallback(async () => {
    if (!isAuthenticated) return;
    if (refreshLocks.current.profile) return; // ← évite double appel
    refreshLocks.current.profile = true;
    try {
      const profileRes = await profileApi.getProfile();
      setData((prevData) => {
        if (!prevData) {
          return {
            profile: (profileRes.data as any) ?? null,
            emails: [],
            phones: [],
            sessions: [],
            devices: [],
            preferences: null,
          };
        }
        return {
          ...prevData,
          profile: (profileRes.data as any) ?? null,
        };
      });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Erreur lors du rafraîchissement du profil",
      );
    } finally {
      refreshLocks.current.profile = false;
    }
  }, [isAuthenticated]);

  const refreshEmails = useCallback(async () => {
    if (!isAuthenticated) return;
    if (refreshLocks.current.emails) return;
    refreshLocks.current.emails = true;
    try {
      const emailsRes = await profileApi.getEmails();
      setData((prevData) => {
        if (!prevData) {
          return {
            profile: null,
            emails: emailsRes.data ?? [],
            phones: [],
            sessions: [],
            devices: [],
            preferences: null,
          };
        }
        return {
          ...prevData,
          emails: emailsRes.data ?? [],
        };
      });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Erreur lors du rafraîchissement des emails",
      );
    } finally {
      refreshLocks.current.emails = false;
    }
  }, [isAuthenticated]);

  const refreshPhones = useCallback(async () => {
    if (!isAuthenticated) return;
    if (refreshLocks.current.phones) return;
    refreshLocks.current.phones = true;
    try {
      const phonesRes = await profileApi.getPhones();
      setData((prevData) => {
        if (!prevData) {
          return {
            profile: null,
            emails: [],
            phones: phonesRes.data ?? [],
            sessions: [],
            devices: [],
            preferences: null,
          };
        }
        return {
          ...prevData,
          phones: phonesRes.data ?? [],
        };
      });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Erreur lors du rafraîchissement des téléphones",
      );
    } finally {
      refreshLocks.current.phones = false;
    }
  }, [isAuthenticated]);

  const refreshSessions = useCallback(async () => {
    if (!isAuthenticated) return;
    if (refreshLocks.current.sessions) return;
    refreshLocks.current.sessions = true;
    try {
      const sessionsRes = await securityApi.getSessions();
      setData((prevData) => {
        if (!prevData) {
          return {
            profile: null,
            emails: [],
            phones: [],
            sessions: (sessionsRes.data as any) ?? [],
            devices: [],
            preferences: null,
          };
        }
        return {
          ...prevData,
          sessions: (sessionsRes.data as any) ?? [],
        };
      });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Erreur lors du rafraîchissement des sessions",
      );
    } finally {
      refreshLocks.current.sessions = false;
    }
  }, [isAuthenticated]);

  const refreshDevices = useCallback(async () => {
    if (!isAuthenticated) return;
    if (refreshLocks.current.devices) return;
    refreshLocks.current.devices = true;
    try {
      const devicesRes = await securityApi.getDevices();
      setData((prevData) => {
        if (!prevData) {
          return {
            profile: null,
            emails: [],
            phones: [],
            sessions: [],
            devices: (devicesRes.data as any) ?? [],
            preferences: null,
          };
        }
        return {
          ...prevData,
          devices: (devicesRes.data as any) ?? [],
        };
      });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Erreur lors du rafraîchissement des appareils",
      );
    } finally {
      refreshLocks.current.devices = false;
    }
  }, [isAuthenticated]);

  const refreshPreferences = useCallback(async () => {
    if (!isAuthenticated) return;
    if (refreshLocks.current.preferences) return;
    refreshLocks.current.preferences = true;
    try {
      const preferencesRes = await preferencesApi.getPreferences();
      setData((prevData) => {
        if (!prevData) {
          return {
            profile: null,
            emails: [],
            phones: [],
            sessions: [],
            devices: [],
            preferences: preferencesRes.data ?? null,
          };
        }
        return {
          ...prevData,
          preferences: preferencesRes.data ?? null,
        };
      });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Erreur lors du rafraîchissement des préférences",
      );
    } finally {
      refreshLocks.current.preferences = false;
    }
  }, [isAuthenticated]);

  // Chargement initial
  useEffect(() => {
    if (status === "initializing" || !isAuthenticated) return;
    if (initialLoadRef.current) return;
    initialLoadRef.current = true;
    void loadAllData();
  }, [status, isAuthenticated, loadAllData]);

  // Reset logout
  useEffect(() => {
    if (isAuthenticated) return;
    setData(null);
    setError(null);
    setLoading(false);
    initialLoadRef.current = false;
    isLoadingRef.current = false;
    // Réinitialiser les locks
    refreshLocks.current = {
      profile: false,
      emails: false,
      phones: false,
      sessions: false,
      devices: false,
      preferences: false,
    };
  }, [isAuthenticated]);

  // Refresh complet
  const refresh = useCallback(async () => {
    if (!isAuthenticated) return;
    await loadAllData();
  }, [isAuthenticated, loadAllData]);

  // Update Profile (appelle refreshProfile)
  const updateProfile = useCallback(
    async (updates: ProfileUpdateRequest) => {
      if (!isAuthenticated) return;
      await profileApi.updateProfile(updates);
      await refreshProfile(); // rafraîchit seulement le profil
    },
    [isAuthenticated, refreshProfile],
  );

  const value: MyAccountContextType = {
    data,
    loading,
    error,
    refresh,
    refreshProfile,
    refreshEmails,
    refreshPhones,
    refreshSessions,
    refreshDevices,
    refreshPreferences,
    updateProfile,
  };

  return (
    <MyAccountContext.Provider value={value}>
      {children}
    </MyAccountContext.Provider>
  );
};

export const useMyAccount = () => {
  const context = useContext(MyAccountContext);
  if (!context) {
    throw new Error("useMyAccount must be used within MyAccountProvider");
  }
  return context;
};